import {
  Button,
  Flex,
  Image,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Textarea,
  Avatar,
  Heading,
  ModalCloseButton,
  FormControl,
  FormLabel,
  ButtonGroup,
  Icon
} from '@homebotapp/hb-react-component-catalog'
import type { ButtonProps } from '@homebotapp/hb-react-component-catalog'
import { defineMessages, useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { useState } from 'react'
import { selectScheduleACallUri } from '../../../store/selectors/customerProfile'
import { VerificationFlowProvider, VerificationFlowManager, useVerificationFlow } from '../../shared/VerificationFlow'
import {
  selectPreferredLoanOfficerMembership,
  selectPreferredRealEstateAgentMembership
} from '../../../store/selectors/clientTeamMemberships'
import { TopicKey } from '../../../constants/customerMessages'
import { PartnerType } from '../../shared/VerificationFlow/types'
import { useCustomerBranding } from '../../../hooks/homebotNetwork/usePreferredAgentOrCustomer'

const MSG = defineMessages({
  buyerPerksDm: {
    id: 'GenericVerificationDirectMessage.cta',
    defaultMessage: 'Have a question?'
  },
  modalTitle: {
    id: 'GenericVerificationDirectMessage.modalTitle',
    defaultMessage: 'Send a message to {name}'
  },
  messagePlaceholder: {
    id: 'GenericVerificationDirectMessage.messagePlaceholder',
    defaultMessage: 'Type your message here...'
  },
  send: {
    id: 'GenericVerificationDirectMessage.send',
    defaultMessage: 'Send'
  },
  cancel: {
    id: 'GenericVerificationDirectMessage.cancel',
    defaultMessage: 'Cancel'
  },
  scheduleCall: {
    id: 'GenericVerificationDirectMessage.scheduleCall',
    defaultMessage: 'Schedule a call'
  }
})

export interface GenericVerificationDirectMessageProps {
  defaultTopicKey: TopicKey
  verifiedTopicKey: TopicKey
  message?: string
  buttonText?: string
  includePrequal?: boolean
  showModal?: boolean
  modalTitle?: string
  messagePlaceholder?: string
  partnerType?: PartnerType
  metadata?: Record<string, any>
  buttonProps?: Partial<ButtonProps> & {
    showAvatar?: boolean
  }
}

// Internal component that uses the verification flow
const GenericVerificationDirectMessageInternal = ({
  defaultTopicKey,
  verifiedTopicKey,
  message = 'Can you tell me more about this listing gallery?',
  buttonText,
  includePrequal = false,
  showModal = false,
  modalTitle,
  messagePlaceholder,
  partnerType = 'LoanOfficer',
  metadata,
  buttonProps = {}
}: GenericVerificationDirectMessageProps) => {
  const intl = useIntl()
  const { startVerification } = useVerificationFlow()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalMessage, setModalMessage] = useState(message)
  const branding = useCustomerBranding(partnerType)
  const photoUri = branding?.photoUri
  const firstName = branding?.firstName
  const scheduleACallUri = useSelector(selectScheduleACallUri)

  const handleButtonClick = () => {
    if (showModal) {
      setIsModalOpen(true)
    } else {
      if (metadata) {
        startVerification(message, verifiedTopicKey, defaultTopicKey, partnerType, metadata)
      } else {
        startVerification(message, verifiedTopicKey, defaultTopicKey, partnerType)
      }
    }
  }

  const handleModalSend = () => {
    setIsModalOpen(false)
    if (metadata) {
      startVerification(modalMessage, verifiedTopicKey, defaultTopicKey, partnerType, metadata)
    } else {
      startVerification(modalMessage, verifiedTopicKey, defaultTopicKey, partnerType)
    }
  }

  const handleModalCancel = () => {
    setIsModalOpen(false)
    setModalMessage(message) // Reset to original message
  }

  const handleScheduleCall = () => {
    if (scheduleACallUri) {
      window.open(scheduleACallUri, '_blank', 'noopener,noreferrer')
    }
    setIsModalOpen(false)
  }

  const displayText = buttonText || intl.formatMessage(MSG.buyerPerksDm, { name: firstName })
  const displayModalTitle = modalTitle || intl.formatMessage(MSG.modalTitle, { name: firstName })
  const displayPlaceholder = messagePlaceholder || intl.formatMessage(MSG.messagePlaceholder)

  // Extract custom props and set defaults based on established DM component patterns
  const {
    showAvatar = true, // Most DM components show customer avatar by default
    leftIcon,
    tracking = {
      guid: '8swvuJPai8GQmXBDP3LoBz',
      ui_context: 'Buyer Verification DM',
      descriptor: showModal ? 'Open message modal' : 'Direct verification flow'
    },
    ...restButtonProps
  } = buttonProps

  // Apply button styling with proper override order
  const buttonColorScheme = restButtonProps.colorScheme || 'secondary'
  const buttonVariant = restButtonProps.variant || 'outline'
  const buttonSize = restButtonProps.size || 'md'
  const buttonWidth = restButtonProps.width || '100%'

  // Determine the left icon to use
  const getLeftIcon = () => {
    if (leftIcon) {
      return leftIcon
    }
    if (showAvatar && photoUri) {
      return <Image src={photoUri} h={6} w={6} borderRadius='50%' />
    }
    return undefined
  }

  return (
    <>
      <Flex
        direction='column'
        align={['unset', 'center']}
        maxW={['100%', '25rem']}
        w='100%'
        background='neutral.200'
        borderRadius='md'
        flexShrink={0}
      >
        <Button
          onClick={handleButtonClick}
          leftIcon={getLeftIcon()}
          colorScheme={buttonColorScheme}
          variant={buttonVariant}
          size={buttonSize}
          tracking={tracking}
          alignItems='center'
          justifyContent='center'
          display='flex'
          p='8px'
          width='100%'
          {...restButtonProps}
        >
          {displayText}
        </Button>
      </Flex>

      {showModal && (
        <Modal isOpen={isModalOpen} onClose={handleModalCancel} size='md'>
          <ModalOverlay />
          <ModalContent>
            <ModalCloseButton />
            <ModalHeader>
              <Flex gap='2.5' alignItems='center'>
                <Avatar size='sm' src={photoUri} showBorder={false} />
                <Heading size='sm' m='0'>
                  {displayModalTitle}
                </Heading>
              </Flex>
            </ModalHeader>
            <ModalBody py='4'>
              <FormControl>
                <FormLabel srOnly>{intl.formatMessage(MSG.messagePlaceholder)}</FormLabel>
                <Textarea
                  name='modalMessage'
                  value={modalMessage}
                  onChange={e => setModalMessage(e.target.value)}
                  placeholder={displayPlaceholder}
                  minHeight='13rem'
                  resize='vertical'
                  tracking={{
                    guid: '1swvuJPai8GQmXBDP3LoBz',
                    ui_context: 'Message Modal',
                    descriptor: 'Message textarea'
                  }}
                />
              </FormControl>
            </ModalBody>
            <ModalFooter>
              <ButtonGroup gap='2' alignItems='center'>
                {scheduleACallUri && (
                  <Button
                    variant='ghost'
                    colorScheme='brand'
                    size='sm'
                    onClick={handleScheduleCall}
                    tracking={{
                      guid: '9swvuJPai8GQmXBDP3LoBz',
                      ui_context: 'Message Modal',
                      descriptor: 'Schedule a call'
                    }}
                  >
                    {intl.formatMessage(MSG.scheduleCall)}
                  </Button>
                )}
                <Button
                  colorScheme='primary'
                  size='sm'
                  onClick={handleModalSend}
                  isDisabled={!modalMessage.trim()}
                  rightIcon={<Icon name='paper-plane' />}
                  tracking={{
                    guid: '0swvuJPai8GQmXBDP3LoBz',
                    ui_context: 'Message Modal',
                    descriptor: 'Send message and start verification'
                  }}
                >
                  {intl.formatMessage(MSG.send)}
                </Button>
              </ButtonGroup>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </>
  )
}

export const GenericVerificationDirectMessage = (props: GenericVerificationDirectMessageProps) => {
  const loanOfficerMembership = useSelector(selectPreferredLoanOfficerMembership)
  const realEstateAgentMembership = useSelector(selectPreferredRealEstateAgentMembership)

  const partnerType = props.partnerType || 'LoanOfficer'

  return (
    <VerificationFlowProvider
      options={{
        enableVerificationFlow: true,
        initialPartnerType: partnerType,
        loanOfficerMembership: loanOfficerMembership,
        realEstateAgentMembership: realEstateAgentMembership,
        includePrequal: props.includePrequal
      }}
    >
      <VerificationFlowManager />
      <GenericVerificationDirectMessageInternal {...props} />
    </VerificationFlowProvider>
  )
}
